import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  UserPlus, X, Users, Phone, Stethoscope, Calendar,
  Activity, Key, BookOpen, Edit2, Cake, Ruler, MapPin
} from "lucide-react";
import { CANCER_TYPES, useTreatmentReferentials } from "@/components/shared/OncologyContext";

export const ROLES = [
  { value: "IDE", label: "Infirmier(e)" },
  { value: "MEDECIN", label: "Medecin" },
  { value: "SECRETAIRE_MEDICAL", label: "Secretaire Medical" },
  { value: "ADMIN_CLINIQUE", label: "Admin Clinique" },
];

/**
 * Format an API error payload into a readable toast message.
 * Handles Pydantic 422 arrays (list of { msg, loc }) gracefully.
 */
export const formatApiError = (err, fallback = "Erreur") => {
  // Network / CORS / no response
  if (!err?.response) {
    if (err?.message) return `Erreur réseau : ${err.message}`;
    return "Erreur réseau : serveur injoignable";
  }
  const status = err.response.status;
  const detail = err.response.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => {
      const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== "body").join(".") : "";
      const msg = d?.msg || d?.message;
      return loc ? `${loc} : ${msg}` : msg;
    }).filter(Boolean);
    if (msgs.length) return msgs.join(" · ");
  }
  // Known HTTP statuses with friendly fallback
  if (status === 409) return "Email déjà utilisé par un autre compte";
  if (status === 403) return "Action non autorisée pour votre rôle";
  if (status === 401) return "Session expirée, reconnectez-vous";
  if (status === 422) return "Données invalides, vérifiez les champs";
  if (status >= 500) return `Erreur serveur (${status}) — merci de réessayer dans quelques secondes`;
  return `${fallback} (HTTP ${status})`;
};

export function CreatePatientModal({ doctors, nurses, secretaries = [], onClose, onCreated }) {
  const [form, setForm] = useState({
    firstName: "", lastName: "", phone: "", email: "", sex: "", dateOfBirth: "",
    preferredLanguage: "Français", address: "",
    doctorIds: [], nurseIds: [], secretaireIds: [],
    programEntryDate: new Date().toISOString().split("T")[0],
    patientAccessEnabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [activationCode, setActivationCode] = useState(null);
  const [createdPatientName, setCreatedPatientName] = useState("");

  const normalizePhone = (raw) => {
    let p = raw.replace(/[\s.\-()]/g, "");
    if (p.startsWith("00212")) p = "+212" + p.slice(5);
    else if (p.startsWith("0")) p = "+212" + p.slice(1);
    else if (p.startsWith("212") && !p.startsWith("+")) p = "+" + p;
    else if (/^[5-7]\d{8}$/.test(p)) p = "+212" + p;
    return p;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.phone) {
      toast.error("Veuillez remplir les champs obligatoires");
      return;
    }
    setLoading(true);
    try {
      const normalizedPhone = normalizePhone(form.phone);
      const payload = {
        firstName: form.firstName, lastName: form.lastName, phone: normalizedPhone,
        email: form.email || null, sex: form.sex || null,
        dateOfBirth: form.dateOfBirth || null,
        birthYear: form.dateOfBirth ? parseInt(form.dateOfBirth.slice(0, 4), 10) : null,
        preferredLanguage: form.preferredLanguage, address: form.address || null,
        doctorIds: form.doctorIds.length > 0 ? form.doctorIds : null,
        nurseIds: form.nurseIds.length > 0 ? form.nurseIds : null,
        secretaireIds: form.secretaireIds.length > 0 ? form.secretaireIds : null,
        programEntryDate: form.programEntryDate,
        patientAccessEnabled: form.patientAccessEnabled,
        dossierStatus: "evaluation_requise", clinicalEvaluationComplete: false, status: "STABLE",
      };
      const res = await api.post("/patients", payload);
      if (res.data?.activationCode) {
        setActivationCode(res.data.activationCode);
        setCreatedPatientName(`${res.data.firstName || form.firstName} ${res.data.lastName || form.lastName}`.trim());
        toast.success("Dossier patient créé. Code d’accès généré.");
        return;
      } else {
        toast.success("Dossier patient cree. L'IDE doit maintenant completer l'evaluation clinique.");
      }
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err, "Erreur lors de la creation"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-patient-modal">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-emerald-600" />
              Nouveau dossier patient
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <p className="text-sm text-emerald-700 mt-2">
            Donnees administratives uniquement. L'evaluation clinique sera completee par l'IDE.
          </p>
        </div>
        {activationCode ? (
          <div className="p-6">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="patient-activation-code-card">
              <p className="text-sm font-semibold text-emerald-900">Code d’accès patient</p>
              {createdPatientName && <p className="text-sm text-emerald-700 mt-1">{createdPatientName}</p>}
              <p className="text-4xl font-black tracking-widest text-emerald-800 mt-3" data-testid="patient-activation-code-value">{activationCode}</p>
              <p className="text-xs text-emerald-700 mt-3">À remettre au patient en clinique. Il devra créer son code personnel à la première connexion.</p>
            </div>
            <button type="button" onClick={onCreated} className="w-full mt-4 py-3 bg-emerald-700 text-white rounded-xl font-semibold" data-testid="patient-activation-code-done-button">
              Code remis — fermer
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Identite</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Prenom *</label>
                <input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-firstName" required />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Nom *</label>
                <input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-lastName" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Sexe</label>
                <select value={form.sex} onChange={e => setForm(f => ({ ...f, sex: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-sex">
                  <option value="">Selectionner...</option>
                  <option value="Homme">Homme</option>
                  <option value="Femme">Femme</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Date de naissance</label>
                <input type="date" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-date-of-birth" />
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Phone className="w-4 h-4" /> Contact</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Téléphone * (connexion Hana)</label>
                <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+212600000000" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-phone" required />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Email (optionnel)</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="patient@email.com" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-email" />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1">Langue preferee</label>
              <select value={form.preferredLanguage} onChange={e => setForm(f => ({ ...f, preferredLanguage: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-language">
                <option value="Français">Français</option>
                <option value="Arabe">Arabe</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1">Adresse</label>
              <textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Adresse complete..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-16" data-testid="patient-address" />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Stethoscope className="w-4 h-4" /> Rattachements</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Oncologues referents</label>
                <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1" data-testid="patient-doctorIds">
                  {doctors.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucun medecin disponible</p>) : doctors.map(d => (
                    <label key={d.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                      <input type="checkbox" checked={form.doctorIds.includes(d.id)} onChange={e => { setForm(f => ({ ...f, doctorIds: e.target.checked ? [...f.doctorIds, d.id] : f.doctorIds.filter(id => id !== d.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                      <span>Dr. {d.lastName} {d.firstName}</span>
                    </label>
                  ))}
                </div>
                {form.doctorIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.doctorIds.length} selectionne(s)</p>)}
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">IDE referent(e)s</label>
                <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1" data-testid="patient-nurseIds">
                  {nurses.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucun IDE disponible</p>) : nurses.map(n => (
                    <label key={n.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                      <input type="checkbox" checked={form.nurseIds.includes(n.id)} onChange={e => { setForm(f => ({ ...f, nurseIds: e.target.checked ? [...f.nurseIds, n.id] : f.nurseIds.filter(id => id !== n.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                      <span>{n.firstName} {n.lastName}</span>
                    </label>
                  ))}
                </div>
                {form.nurseIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.nurseIds.length} selectionne(s)</p>)}
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1">Secretaire(s) medicale(s) referente(s)</label>
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1" data-testid="patient-secretaireIds">
                {secretaries.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucune secretaire disponible</p>) : secretaries.map(s => (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                    <input type="checkbox" checked={form.secretaireIds.includes(s.id)} onChange={e => { setForm(f => ({ ...f, secretaireIds: e.target.checked ? [...f.secretaireIds, s.id] : f.secretaireIds.filter(id => id !== s.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span>{s.firstName} {s.lastName}</span>
                  </label>
                ))}
              </div>
              {form.secretaireIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.secretaireIds.length} selectionnee(s)</p>)}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" /> Programme</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Date d'entree dans le programme</label>
                <input type="date" value={form.programEntryDate} onChange={e => setForm(f => ({ ...f, programEntryDate: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="patient-programEntryDate" />
              </div>
              <div className="flex items-center pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.patientAccessEnabled} onChange={e => setForm(f => ({ ...f, patientAccessEnabled: e.target.checked }))} className="w-4 h-4 text-emerald-600 rounded" data-testid="patient-accessEnabled" />
                  <span className="text-sm text-slate-600">Acces patient active</span>
                </label>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Activity className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Prochaine etape:</strong> Apres la creation, l'IDE referent(e) devra completer l'evaluation clinique initiale (type de cancer, stade, traitement...) pour activer le dossier.
            </div>
          </div>
          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50">Annuler</button>
            <button type="submit" disabled={loading} data-testid="submit-create-patient" className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50">{loading ? "Creation..." : "Creer le dossier"}</button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export function EditPatientModal({ patient, doctors, nurses, secretaries = [], onClose, onUpdated }) {
  const { treatmentTypes, therapeuticLines } = useTreatmentReferentials();
  const hasLegacyOnly = patient.pathology || patient.treatment;
  const hasNewFields = patient.cancerType || patient.treatmentType || patient.therapeuticLine || patient.protocol;
  const [form, setForm] = useState({
    firstName: patient.firstName || "", lastName: patient.lastName || "",
    phone: patient.phone || "",
    pathology: patient.pathology || "", treatment: patient.treatment || "",
    cancerType: patient.cancerType || "", cancerTypeDetail: patient.cancerTypeDetail || "",
    treatmentType: patient.treatmentType || "", therapeuticLine: patient.therapeuticLine || "",
    protocol: patient.protocol || "",
    doctorIds: patient.doctorIds || (patient.doctorId ? [patient.doctorId] : []),
    nurseIds: patient.nurseIds || (patient.nurseId ? [patient.nurseId] : []),
    secretaireIds: patient.secretaireIds || [],
    height: patient.height || "", dateOfBirth: patient.dateOfBirth || "", address: patient.address || "",
  });
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        firstName: form.firstName, lastName: form.lastName,
        phone: form.phone || null,
        cancerType: form.cancerType || null, cancerTypeDetail: form.cancerTypeDetail || null,
        treatmentType: form.treatmentType || null, therapeuticLine: form.therapeuticLine || null,
        protocol: form.protocol || null,
        doctorIds: form.doctorIds.length > 0 ? form.doctorIds : null,
        nurseIds: form.nurseIds.length > 0 ? form.nurseIds : null,
        secretaireIds: form.secretaireIds,
        height: form.height ? parseFloat(form.height) : null,
        dateOfBirth: form.dateOfBirth || null, address: form.address || null,
      };
      await api.put(`/patients/${patient.id}`, payload);
      toast.success("Patient mis a jour");
      onUpdated();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResetAccessCode = async () => {
    if (!confirm("Générer un nouveau code d’accès pour ce patient ?")) return;
    try {
      const res = await api.post(`/patients/${patient.id}/reset-access-code`);
      setAccessCode(res.data.activationCode);
      toast.success("Nouveau code patient généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de générer le code");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-patient-modal">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800">Modifier le patient</h2>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-4" data-testid="edit-patient-access-code-section">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-sky-900">Accès patient Hana</p>
                <p className="text-xs text-sky-700 mt-1">Générer un code à remettre en clinique si le patient n’a pas encore son accès.</p>
              </div>
              <button type="button" onClick={handleResetAccessCode} className="px-3 py-2 bg-sky-700 text-white rounded-lg text-sm font-semibold" data-testid="edit-patient-reset-access-code-button">
                Générer code
              </button>
            </div>
            {accessCode && (
              <div className="mt-4 rounded-xl bg-white border border-sky-200 p-4 text-center" data-testid="edit-patient-access-code-card">
                <p className="text-xs font-semibold text-sky-800 uppercase tracking-wide">Code d’accès</p>
                <p className="text-3xl font-black tracking-widest text-sky-900 mt-2" data-testid="edit-patient-access-code-value">{accessCode}</p>
                <p className="text-xs text-sky-700 mt-2">À remettre au patient. Création du code personnel obligatoire à la première connexion.</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Prenom</label><input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Nom</label><input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Téléphone (connexion Hana)</label>
            <input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+212600000000" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="edit-patient-phone" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1 flex items-center gap-1"><Cake className="w-3 h-3" /> Date de naissance</label><input type="date" value={form.dateOfBirth} onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs text-slate-500 mb-1 flex items-center gap-1"><Ruler className="w-3 h-3" /> Taille (cm)</label><input type="number" value={form.height} onChange={e => setForm(f => ({ ...f, height: e.target.value }))} placeholder="170" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse</label><textarea value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Adresse complete..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none h-16" /></div>
          {hasLegacyOnly && !hasNewFields && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 mb-2">Ancien format detecte. Les nouvelles donnees structurees remplaceront les champs actuels.</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-amber-600">
                <div><span className="font-medium">Pathologie actuelle:</span> {patient.pathology || "\u2014"}</div>
                <div><span className="font-medium">Traitement actuel:</span> {patient.treatment || "\u2014"}</div>
              </div>
            </div>
          )}
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100">
            <h3 className="text-sm font-semibold text-blue-800 mb-4 flex items-center gap-2"><Activity className="w-4 h-4" /> Contexte oncologique</h3>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div><label className="block text-xs text-slate-600 mb-1">Type de cancer</label><select value={form.cancerType} onChange={e => setForm(f => ({ ...f, cancerType: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"><option value="">Selectionner...</option>{CANCER_TYPES.map(type => (<option key={type} value={type}>{type}</option>))}</select></div>
              <div><label className="block text-xs text-slate-600 mb-1">Type de traitement</label><select value={form.treatmentType} onChange={e => setForm(f => ({ ...f, treatmentType: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"><option value="">Selectionner...</option>{treatmentTypes.map(type => (<option key={type} value={type}>{type}</option>))}</select></div>
              <div><label className="block text-xs text-slate-600 mb-1">Ligne therapeutique</label><select value={form.therapeuticLine} onChange={e => setForm(f => ({ ...f, therapeuticLine: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"><option value="">Selectionner...</option>{therapeuticLines.map(line => (<option key={line} value={line}>{line}</option>))}</select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-slate-600 mb-1">Protocole</label><input type="text" value={form.protocol} onChange={e => setForm(f => ({ ...f, protocol: e.target.value }))} placeholder="Ex: FOLFOX, KEYTRUDA..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" /></div>
              <div><label className="block text-xs text-slate-600 mb-1">Detail type de cancer</label><input type="text" value={form.cancerTypeDetail} onChange={e => setForm(f => ({ ...f, cancerTypeDetail: e.target.value }))} placeholder="Ex: HER2+, triple negatif..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" /></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Medecins referents</label>
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1">
                {doctors.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucun medecin</p>) : doctors.map(d => (
                  <label key={d.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                    <input type="checkbox" checked={form.doctorIds.includes(d.id)} onChange={e => { setForm(f => ({ ...f, doctorIds: e.target.checked ? [...f.doctorIds, d.id] : f.doctorIds.filter(id => id !== d.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span>Dr. {d.lastName} {d.firstName}</span>
                  </label>
                ))}
              </div>
              {form.doctorIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.doctorIds.length} selectionne(s)</p>)}
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">IDE referent(e)s</label>
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1">
                {nurses.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucun IDE</p>) : nurses.map(n => (
                  <label key={n.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                    <input type="checkbox" checked={form.nurseIds.includes(n.id)} onChange={e => { setForm(f => ({ ...f, nurseIds: e.target.checked ? [...f.nurseIds, n.id] : f.nurseIds.filter(id => id !== n.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                    <span>{n.firstName} {n.lastName}</span>
                  </label>
                ))}
              </div>
              {form.nurseIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.nurseIds.length} selectionne(s)</p>)}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Secretaire(s) medicale(s) referente(s)</label>
            <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto p-2 space-y-1" data-testid="edit-patient-secretaireIds">
              {secretaries.length === 0 ? (<p className="text-xs text-slate-400 p-1">Aucune secretaire disponible</p>) : secretaries.map(s => (
                <label key={s.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer text-sm">
                  <input type="checkbox" checked={form.secretaireIds.includes(s.id)} onChange={e => { setForm(f => ({ ...f, secretaireIds: e.target.checked ? [...f.secretaireIds, s.id] : f.secretaireIds.filter(id => id !== s.id) })); }} className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>{s.firstName} {s.lastName}</span>
                </label>
              ))}
            </div>
            {form.secretaireIds.length > 0 && (<p className="text-xs text-emerald-600 mt-1">{form.secretaireIds.length} selectionnee(s)</p>)}
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Enregistrer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", password: "", role: "IDE" });
  const [loading, setLoading] = useState(false);
  const [initialPassword, setInitialPassword] = useState(null);
  const [createdUserLabel, setCreatedUserLabel] = useState("");
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) { toast.error("Veuillez remplir tous les champs obligatoires"); return; }
    setLoading(true);
    try {
      const res = await api.post("/users", form);
      setInitialPassword(res.data?.initialPassword || form.password);
      setCreatedUserLabel(`${res.data.firstName || form.firstName} ${res.data.lastName || form.lastName}`.trim());
      toast.success(`${res.data.firstName || ""} ${res.data.lastName || ""} ajouté(e) en tant que ${ROLES.find(r => r.value === form.role)?.label || form.role}`, { duration: 4000 });
      return;
    }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="create-user-modal">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><UserPlus className="w-5 h-5 text-emerald-600" />Nouvel utilisateur</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
        </div>
        {initialPassword ? (
          <div className="p-6 space-y-4">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="staff-initial-password-card">
              <p className="text-sm font-semibold text-amber-900">Mot de passe temporaire</p>
              {createdUserLabel && <p className="text-sm text-amber-700 mt-1">{createdUserLabel}</p>}
              <p className="text-xl font-black tracking-wide text-amber-800 mt-2" data-testid="staff-initial-password-value">{initialPassword}</p>
              <p className="text-xs text-amber-700 mt-2">À transmettre au professionnel. Changement obligatoire à la première connexion, puis création du code Hana.</p>
            </div>
            <button type="button" onClick={onCreated} className="w-full py-3 bg-amber-600 text-white rounded-xl font-semibold" data-testid="staff-initial-password-done-button">
              Accès transmis — fermer
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Prenom *</label><input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-firstName" required /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Nom *</label><input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-lastName" required /></div>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1">Email *</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-email" required /></div>
          <div><label className="block text-xs text-slate-500 mb-1">Telephone</label><input type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-phone" /></div>
          <div><label className="block text-xs text-slate-500 mb-1">Role *</label><select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="user-role">{ROLES.map(r => (<option key={r.value} value={r.value}>{r.label}</option>))}</select></div>
          <div><label className="block text-xs text-slate-500 mb-1">Mot de passe initial</label><input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50" data-testid="user-password" /><p className="text-xs text-slate-400 mt-1">L'utilisateur devra le changer a la premiere connexion</p></div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} data-testid="submit-create-user" className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Creer"}</button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export function EditUserModal({ user, onClose, onUpdated }) {
  const [form, setForm] = useState({ firstName: user.firstName || "", lastName: user.lastName || "", email: user.email || "", isActive: user.isActive });
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.put(`/users/${user.id}`, form); toast.success("Utilisateur mis a jour"); onUpdated(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" data-testid="edit-user-modal">
        <div className="p-6 border-b"><div className="flex items-center justify-between"><h2 className="text-lg font-bold text-slate-800">Modifier l'utilisateur</h2><button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button></div></div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Prenom</label><input type="text" value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Nom</label><input type="text" value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1">Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div>
          <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <label className="text-sm text-slate-700">Utilisateur actif</label>
            <button type="button" onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))} className={`w-12 h-6 rounded-full transition-colors ${form.isActive ? "bg-green-500" : "bg-slate-300"}`}><div className={`w-5 h-5 bg-white rounded-full transition-transform ${form.isActive ? "translate-x-6" : "translate-x-0.5"}`} /></button>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Enregistrer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ResetPasswordModal({ user, onClose, onReset }) {
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.patch(`/users/${user.id}/reset-password`, {});
      setTempPassword(res.data.temporaryPassword);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(tempPassword); toast.success("Copié"); } catch { toast.error("Copie impossible"); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="reset-password-modal">
        <div className="p-6 border-b bg-amber-50"><div className="flex items-center justify-between"><h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Key className="w-5 h-5 text-amber-600" />Réinitialiser le mot de passe</h2><button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg" data-testid="reset-password-close"><X className="w-5 h-5 text-slate-400" /></button></div></div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">Compte : <strong>{user.firstName} {user.lastName}</strong> ({user.role})</p>
          {!tempPassword ? (
            <>
              <p className="text-xs text-slate-500">Un mot de passe temporaire sera généré par Hana et affiché une seule fois. L'utilisateur devra choisir un nouveau mot de passe et un nouveau code Hana à sa prochaine connexion ; ses appareils de confiance seront révoqués et il sera notifié.</p>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
                <button type="button" onClick={generate} disabled={loading} data-testid="submit-reset-password" className="flex-1 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium">{loading ? "..." : "Générer un mot de passe temporaire"}</button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                <p className="text-xs text-slate-500 mb-1">Mot de passe temporaire (affiché une seule fois)</p>
                <p className="font-mono text-xl font-bold tracking-wider text-slate-800" data-testid="temporary-password">{tempPassword}</p>
                <button type="button" onClick={copy} data-testid="copy-temporary-password" className="mt-3 text-xs font-medium text-amber-700 hover:underline">Copier</button>
              </div>
              <p className="text-xs text-slate-500">Transmettez-le de vive voix ou par un canal sécurisé. Il expire dès la première connexion.</p>
              <button type="button" onClick={onReset} data-testid="reset-password-done" className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium">Terminé</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function EducationContentModal({ content, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: content?.title || "", slug: content?.slug || "",
    shortDescription: content?.shortDescription || "", bodyText: content?.bodyText || "",
    contentType: content?.contentType || "VIDEO", category: content?.category || "BIENVENUE",
    youtubeUrl: content?.youtubeUrl || "", durationSeconds: content?.durationSeconds || 180,
    keyPoints: content?.keyPoints?.join("\n") || "",
    targetTreatments: content?.targetTreatments?.join(", ") || "",
    targetSymptoms: content?.targetSymptoms?.join(", ") || "",
    priority: content?.priority || 0, status: content?.status || "PUBLISHED",
  });
  const categories = [
    { value: "BIENVENUE", label: "Bienvenue" }, { value: "TRAITEMENT", label: "Traitement" },
    { value: "NUTRITION", label: "Nutrition" }, { value: "EFFETS_SECONDAIRES", label: "Effets secondaires" },
    { value: "SIGNES_ALERTE", label: "Signes d'alerte" }, { value: "BIEN_VIVRE", label: "Bien-etre" },
    { value: "GESTION_SYMPTOMES", label: "Gestion des symptomes" },
  ];
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) { toast.error("Titre et slug sont obligatoires"); return; }
    setLoading(true);
    try {
      const payload = {
        title: form.title, slug: form.slug, shortDescription: form.shortDescription,
        bodyText: form.bodyText || null, contentType: form.contentType, category: form.category,
        youtubeUrl: form.youtubeUrl || null, durationSeconds: parseInt(form.durationSeconds) || 180,
        keyPoints: form.keyPoints ? form.keyPoints.split("\n").filter(k => k.trim()) : [],
        targetTreatments: form.targetTreatments ? form.targetTreatments.split(",").map(s => s.trim()).filter(s => s) : [],
        targetSymptoms: form.targetSymptoms ? form.targetSymptoms.split(",").map(s => s.trim()).filter(s => s) : [],
        priority: parseInt(form.priority) || 0,
      };
      if (content?.id) { payload.status = form.status; await api.put(`/education/admin/contents/${content.id}`, payload); toast.success("Contenu mis a jour"); }
      else { await api.post("/education/admin/contents", payload); toast.success("Contenu cree"); }
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };
  const generateSlug = (title) => title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        <div className="p-6 border-b bg-emerald-50">
          <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-slate-800 flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-600" />{content ? "Modifier le contenu" : "Nouveau contenu educatif"}</h2><button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button></div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Titre *</label><input type="text" value={form.title} onChange={e => { setForm({ ...form, title: e.target.value, slug: generateSlug(e.target.value) }); }} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" required /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Slug *</label><input type="text" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" required /></div>
          </div>
          <div><label className="block text-xs text-slate-500 mb-1">Description courte *</label><input type="text" value={form.shortDescription} onChange={e => setForm({ ...form, shortDescription: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Type de contenu</label><select value={form.contentType} onChange={e => setForm({ ...form, contentType: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="VIDEO">Video</option><option value="ARTICLE">Article</option><option value="PACK">Pack</option></select></div>
            <div><label className="block text-xs text-slate-500 mb-1">Categorie</label><select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">{categories.map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}</select></div>
          </div>
          {form.contentType === "VIDEO" && (<div className="grid grid-cols-2 gap-4"><div><label className="block text-xs text-slate-500 mb-1">URL YouTube</label><input type="url" value={form.youtubeUrl} onChange={e => setForm({ ...form, youtubeUrl: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="https://youtu.be/..." /></div><div><label className="block text-xs text-slate-500 mb-1">Duree (secondes)</label><input type="number" value={form.durationSeconds} onChange={e => setForm({ ...form, durationSeconds: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></div></div>)}
          <div><label className="block text-xs text-slate-500 mb-1">Texte detaille</label><textarea value={form.bodyText} onChange={e => setForm({ ...form, bodyText: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm h-24" placeholder="Contenu detaille de l'article..." /></div>
          <div><label className="block text-xs text-slate-500 mb-1">Points cles (un par ligne)</label><textarea value={form.keyPoints} onChange={e => setForm({ ...form, keyPoints: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm h-20" placeholder={"Point 1\nPoint 2\nPoint 3"} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Traitements cibles (virgules)</label><input type="text" value={form.targetTreatments} onChange={e => setForm({ ...form, targetTreatments: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="chimio, radiotherapie" /></div>
            <div><label className="block text-xs text-slate-500 mb-1">Symptomes cibles (virgules)</label><input type="text" value={form.targetSymptoms} onChange={e => setForm({ ...form, targetSymptoms: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="fatigue, nausea, mucositis" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-xs text-slate-500 mb-1">Priorite (0-100)</label><input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" min="0" max="100" /></div>
            {content && (<div><label className="block text-xs text-slate-500 mb-1">Statut</label><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="PUBLISHED">Publie</option><option value="DRAFT">Brouillon</option><option value="ARCHIVED">Archive</option></select></div>)}
          </div>
          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">{loading ? "..." : content ? "Mettre a jour" : "Creer"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
